import { useNavigate } from 'react-router-dom';
import { SessionData, StateProperty } from '@app/common';
import React from 'react';
import { getVisibleSections, sessionHasRecommendedSections, sessionIsFinished } from '../../../../services/DecisionTreeService/DecisionTreeService';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { setTransientStateProperties } from '../../../../store/moduleStateSlice';
import TextWithReferences from '../../PageElements/TextWithReferences/TextWithReferences';
import SlotDropdownItemStartButton from './SlotDropdownItemStartButton';

type Properties = {
    session: SessionData;
    sessionId: `session_${string}`;
    currSlotId: `slot_${string}`;
};

const SlotDropdownSessionItem = (props: Properties) => {
    const navigate = useNavigate();
    
    const dispatch = useAppDispatch();
    const stateProperties = useAppSelector(state => state.globalState.user?.stateProperties || []);
    const moduleState = useAppSelector(state => state.moduleState.currentModuleState);
    const config = useAppSelector(state => state.globalState.config);
    const user = useAppSelector(state => state.globalState.user);
    const userProperties = user?.userProperties || [];

    const pregnancyRecommended = stateProperties.includes('PREGNANCY');
    const postpartumRecommended = stateProperties.includes('POSTPARTUM');

    const pregnancyButtonVisible = sessionHasRecommendedSections(props.session, ['PREGNANCY'], userProperties);
    const postpartumButtonVisible = sessionHasRecommendedSections(props.session, ['POSTPARTUM'], userProperties);
    const isTransientEnabled = config?.enableTransientSessionModes === true;
    const noVisibleSectionsMessage = 'Für diese Sitzung sind aktuell keine sichtbaren Inhalte verfügbar.';
    const [canStartNormalSession, setCanStartNormalSession] = React.useState<boolean>(false);
    const [canStartPregnancySession, setCanStartPregnancySession] = React.useState<boolean>(false);
    const [canStartPostpartumSession, setCanStartPostpartumSession] = React.useState<boolean>(false);
    const [isStartAvailabilityLoading, setIsStartAvailabilityLoading] = React.useState<boolean>(true);
    const [showNoVisibleSectionsMessage, setShowNoVisibleSectionsMessage] = React.useState<boolean>(false);

    React.useEffect(() => {
        let isMounted = true;

        const evaluateStartAvailability = async () => {
            setIsStartAvailabilityLoading(true);
            setShowNoVisibleSectionsMessage(false);
            try {
                if (isTransientEnabled) {
                    const [pregnancySections, postpartumSections] = await Promise.all([
                        getVisibleSections(props.session, moduleState.modifiers, moduleState.elementValues, userProperties, ['PREGNANCY']),
                        getVisibleSections(props.session, moduleState.modifiers, moduleState.elementValues, userProperties, ['POSTPARTUM'])
                    ]);

                    if (!isMounted) {
                        return;
                    }

                    setCanStartPregnancySession(pregnancySections.length > 0);
                    setCanStartPostpartumSession(postpartumSections.length > 0);
                    setCanStartNormalSession(false);
                } else {
                    const normalSections = await getVisibleSections(props.session, moduleState.modifiers, moduleState.elementValues, userProperties, []);

                    if (!isMounted) {
                        return;
                    }

                    setCanStartNormalSession(normalSections.length > 0);
                    setCanStartPregnancySession(false);
                    setCanStartPostpartumSession(false);
                }
            } catch (error) {
                console.error('[SlotDropdownSessionItem] Failed to evaluate session start availability.', {
                    sessionId: props.sessionId,
                    slotId: props.currSlotId,
                    error
                });
                if (isMounted) {
                    setCanStartNormalSession(false);
                    setCanStartPregnancySession(false);
                    setCanStartPostpartumSession(false);
                }
            } finally {
                if (isMounted) {
                    setIsStartAvailabilityLoading(false);
                }
            }
        };

        evaluateStartAvailability();
        return () => {
            isMounted = false;
        };
    }, [props.session, props.sessionId, props.currSlotId, moduleState.modifiers, moduleState.elementValues, userProperties, isTransientEnabled]);

    const startSessionWithTransientProperties = async (transientProperties: StateProperty[]) => {
        const visibleSections = await getVisibleSections(props.session, moduleState.modifiers, moduleState.elementValues, userProperties, transientProperties);
        const firstVisibleSectionId = visibleSections[0]?.id;

        if (!firstVisibleSectionId) {
            console.warn('[SlotDropdownSessionItem] Prevented session start because no visible sections are available.', {
                sessionId: props.sessionId,
                slotId: props.currSlotId,
                transientProperties
            });
            setShowNoVisibleSectionsMessage(true);
            return;
        }

        dispatch(setTransientStateProperties(transientProperties));
        navigate(`${props.currSlotId}/${props.sessionId}/${firstVisibleSectionId}`);
    };

    const onStartPregnancySessionClick = async () => {
        await startSessionWithTransientProperties(['PREGNANCY']);
    };

    const onStartPostpartumSessionClick = async () => {
        await startSessionWithTransientProperties(['POSTPARTUM']);
    };

    const onStartNormalSessionClick = async () => {
        await startSessionWithTransientProperties([]);
    };

    const hasPregnancyFinished = sessionIsFinished(props.currSlotId, props.sessionId, ['PREGNANCY'], moduleState?.finished || {}, config?.enableTransientSessionModes === true);
    const hasPostpartumFinished = sessionIsFinished(props.currSlotId, props.sessionId, ['POSTPARTUM'], moduleState?.finished || {}, config?.enableTransientSessionModes === true);
    const hasNormalSessionFinished = sessionIsFinished(props.currSlotId, props.sessionId, [], moduleState?.finished || {}, false);
    const showNoAvailableMessage = showNoVisibleSectionsMessage || (!isStartAvailabilityLoading && (
        isTransientEnabled ? (!canStartPregnancySession && !canStartPostpartumSession) : !canStartNormalSession
    ));

    // Helper to darken hex color for hover state
    const getDarkerColor = (hexColor: string) => {
        const hex = hexColor.replace('#', '');
        const r = Math.max(0, parseInt(hex.substring(0, 2), 16) - 30);
        const g = Math.max(0, parseInt(hex.substring(2, 4), 16) - 30);
        const b = Math.max(0, parseInt(hex.substring(4, 6), 16) - 30);
        return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    const themeColor = config?.themeColor || '#007AFF';
    const darkerThemeColor = getDarkerColor(themeColor);

    return (
        <div className="slot-session" style={{ display: 'flex' }}>
            <div className="session-info">
                <span className="session-name">{props.session.name}</span><br />
                <span className="session-decription"><TextWithReferences text={props.session.desc || ''} /></span>
            </div>

            <div className="session-actions">
                {isTransientEnabled ? (
                    <>
                        <SlotDropdownItemStartButton
                            isHidden={!pregnancyButtonVisible || !canStartPregnancySession}
                            isRecommended={pregnancyRecommended}
                            imagePath={'i-pregno/pregnant-less-intense-with-baby.png'}
                            onClick={onStartPregnancySessionClick}
                            hasCheckMark={hasPregnancyFinished}
                        />
                        <SlotDropdownItemStartButton
                            isHidden={!postpartumButtonVisible || !canStartPostpartumSession}
                            isRecommended={postpartumRecommended}
                            imagePath={'i-pregno/baby-boy.png'}
                            onClick={onStartPostpartumSessionClick}
                            hasCheckMark={hasPostpartumFinished}
                        />
                    </>
                ) : (
                    canStartNormalSession && (
                        <button 
                            className="action-button"
                            onClick={onStartNormalSessionClick}
                            style={{ 
                                padding: '12px 24px', 
                                fontSize: '16px',
                                fontWeight: '600',
                                backgroundColor: themeColor,
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = darkerThemeColor)}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = themeColor)}
                        >
                            Start {hasNormalSessionFinished && '✓'}
                        </button>
                    )
                )}
                {showNoAvailableMessage && <span style={{ color: 'var(--color-gray-600)', fontSize: 'var(--font-size-body-lg)' }}>{noVisibleSectionsMessage}</span>}
            </div>
        </div>
    );
};
export default SlotDropdownSessionItem;